import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AlertController, IonContent, ToastController } from '@ionic/angular';
import { AvailableModelsResponse, CopilotMessage, GenerateFormResponse, GenerateWorkoutResponse } from '@me-fit-mono/typings';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom, map, takeUntil, tap } from 'rxjs';
import { Feedback } from 'src/app/decorators/feedback-decorator';
import { CopilotContextButton, CopilotPaneService, CopilotPaneStateOpen, OpenCopilotPaneFormContext, OpenCopilotPaneOptions, OpenCopilotPaneWorkoutContext } from 'src/app/services/copilot-pane.service';
import { CopilotStateService } from 'src/app/state/copilot.state.service';

@Component({
  selector: 'me-copilot-pane',
  templateUrl: './copilot-pane.component.html',
  styleUrls: ['./copilot-pane.component.css']
})
export class CopilotPaneComponent {
  aiForm = new FormGroup({
    aiPrompt: new FormControl('', {nonNullable: true}),
    model: new FormControl('', { nonNullable: true })
  });

  modelOptions$: Observable<AvailableModelsResponse['models']> = this.$copilot.getAvailableModels.state$.pipe(
    tap( state => {
      if (state.response?.defaultModel) {
        this.aiForm.patchValue({ model: state.response.defaultModel.value });
      }
    }),
    map(state => {
      if (state.response) {
        return state.response.models;
      }

      return [];
    }),
  );

  fields$ = this.$copilotPane.fields$;
  contextButtons$ = this.$copilotPane.contextButtons$;

  // Chat history state
  messages$ = new BehaviorSubject<CopilotMessage[]>([]);

  loading$ = combineLatest([
    this.$copilot.generateWorkout.isLoading$(),
    this.$copilot.generateForm.isLoading$(),
  ]).pipe(
    map(([isGeneratingWorkout]) => isGeneratingWorkout)
  );

  @ViewChild('chatHistory') chatHistoryRef: ElementRef<HTMLDivElement>;

  stop$ = new Subject<void>();

  @ViewChild(IonContent, { static: false }) content: IonContent;



  constructor(
    private $copilotPane: CopilotPaneService,
    private $copilot: CopilotStateService,
    private alertCtrl: AlertController,
    private $translate: TranslateService,
    private toastCtrl: ToastController,
  ) { }

  async ngOnInit() {
    this.loadAvailableModels();
  }

  async loadAvailableModels() {
    try {
      await this.$copilot.getAvailableModels.call();
    } catch (error) {
      console.error('Error loading available models:', error);
    }
  }

  ngAfterViewInit() {
    // Scroll to the bottom of the chat history when it changes
    this.messages$.pipe(
      takeUntil(this.stop$),
    ).subscribe(() => {
      setTimeout(() => {
        this.content.scrollToBottom();
      }, 100);
    });

    this.loading$.pipe(
      takeUntil(this.stop$)
    ).subscribe((isLoading) => {
      if (isLoading) {
        // Scroll to the bottom when loading starts
        setTimeout(() => {
          this.content.scrollToBottom();
        }, 100);
      }
    }
    )
  }

  toggleSideMenuExpand() {}

  ngOnDestroy() {
    this.stop$.next();
  }

  async onContextButtonClick(button: CopilotContextButton) {
    const data = await button.callback();
  }

  async submit() {

    if (this.aiForm.value.aiPrompt?.length == 0) {
      return
    }
    const loading = await firstValueFrom(this.loading$);
    if (loading) {
      // If already loading, do not submit again
      return;
    }

    const value = this.$copilotPane.state$.value as CopilotPaneStateOpen;

    if (this.isWorkoutContext(value.context)) {
      this.generateWorkout(value.context);
    }

    if (this.isFormContext(value.context)) {
      this.generateForm(value.context);
    }
  }


  onTextareaKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  @Feedback({
    i18nFailKey: 'me.fit.copilot.message.fail',
    showLoader: false
  })
  async generateForm(context: OpenCopilotPaneFormContext) {
    const userMessage: CopilotMessage = {
      role: 'user',
      content: {
        text: this.aiForm.value.aiPrompt!
      }
    };

    const modelValue = this.aiForm.value.model ?? 'gpt-4o-mini';

    // Reset form after submission
    this.aiForm.controls.aiPrompt.reset();

    // Get current chat history
    const currentMessages = this.messages$.value;
    const messages = [...currentMessages, userMessage];

    const systemMessage = this.$copilotPane.systemMessage[context.type];
    // If there is a system message, add it to the beginning of the messages
    if (systemMessage && messages.findIndex(msg => msg.role === 'system') === -1) {
      messages.unshift({
        role: 'system',
        content: {
          text: systemMessage
        }
      });
    }

    this.messages$.next(messages);

    const response: GenerateFormResponse = await this.$copilot.generateForm.call({
      request: {
        messages,
        modelValue: modelValue,
        currentForm: context.currentForm ?? undefined
      }
    });

    // Add AI response to chat
    const aiMessage: CopilotMessage = {
      role: 'assistant',
      content: {
        text: response.modelResponse.copilotMessage,
        data: {
          kind: 'generated-form',
          modelResponse: response.modelResponse,
        }
      }
    }

    this.messages$.next([...this.messages$.value, aiMessage]);

    this.$copilotPane.output$.next({
      type: 'form',
      value: response
    });

    return response;
  }

  @Feedback({
    i18nFailKey: 'me.fit.copilot.message.fail',
    showLoader: false
  })
  async generateWorkout(context: OpenCopilotPaneWorkoutContext) {

    const userMessage: CopilotMessage = {
      role: 'user',
      content: {
        text: this.aiForm.value.aiPrompt!,
        data: {
          kind: 'workout-context',
          workout: {
            name: context.name,
            description: context.description,
            steps: context.steps,
          }
        }
      }
    };


    const modelValue = this.aiForm.value.model ?? 'gpt-4o-mini';


    // Reset form after submission
    this.aiForm.controls.aiPrompt.reset();

    // Get current chat history
    const currentMessages = this.messages$.value;
    const messages = [...currentMessages, userMessage];

    const systemMessage = this.$copilotPane.systemMessage[context.type];
    // If there is a system message, add it to the beginning of the messages
    if (systemMessage && messages.findIndex(msg => msg.role === 'system') === -1) {
      messages.unshift({
        role: 'system',
        content: {
          text: systemMessage
        }
      });
    }

    this.messages$.next(messages);

    const response: GenerateWorkoutResponse = await this.$copilot.generateWorkout.call({
      request: {
        customerId: context.customerId,
        messages,
        modelValue: modelValue
      }
    });

    // Add AI response to chat
    const aiMessage: CopilotMessage = {
      role: 'assistant',
      content: {
        text: response.modelResponse.copilotMessage,
        data: response.modelResponse.workout
          ? {
              kind: 'workout-context',
              workout: response.modelResponse.workout,
            }
          : undefined
      }
    }

    this.messages$.next([...this.messages$.value, aiMessage]);

    this.$copilotPane.output$.next({
      type: 'workout',
      value: response
    });

    return response;
  }

  collapsePane() {
    this.$copilotPane.setCollapsed(true);
  }


  isWorkoutContext(value: OpenCopilotPaneOptions['context']): value is OpenCopilotPaneWorkoutContext {
    return value && value.type === 'workout';
  }

  isFormContext(value: OpenCopilotPaneOptions['context']): value is OpenCopilotPaneFormContext {
    return value && value.type === 'form';
  }

  getMessageSystemPromptFromLocalStorage(): string | null {
    const value = localStorage.getItem('copilot-system-prompt');
    return value;
  }

  getSystemMessage() {
    if (!this.$copilotPane.state$.value.open) {
      return;
    }

    const { context } = this.$copilotPane.state$.value;

    const value = this.$copilotPane.systemMessage?.[context.type] || '';

    return value;
  }

  async configure() {
    if (!this.$copilotPane.state$.value.open) {
      return;
    }

    const { context } = this.$copilotPane.state$.value;

    const inputValue = this.getSystemMessage() || '';

    const alert = await this.alertCtrl.create({
      header: this.$translate.instant('me.fit.copilot.configure.title'),
      message: this.$translate.instant('me.fit.copilot.configure.message'),
      buttons: [{
        text: this.$translate.instant('action.reset.to.default'),
        handler: () => {
          this.$copilotPane.resetSystemMessageToDefault();
          this.toastCtrl.create({
            message: this.$translate.instant('me.fit.copilot.configure.reset.success'),
            duration: 3000
          }).then(toast => toast.present());
        },
        role: 'reset'
      },
      {
        text: this.$translate.instant('action.save'),
        role: 'confirm',
        cssClass: 'alert-secondary'
      }],
      inputs: [{
        type: 'textarea',
        label: this.$translate.instant('me.fit.copilot.system.prompt'),
        value: inputValue,
        name: 'systemPrompt',
      }],
      cssClass: 'copilot-system-prompt-alert'
    });

    await alert.present();

    const { data, role } = await alert.onDidDismiss();

    if (role !== 'reset' && data?.values) {
      const systemPrompt: string = data.values['systemPrompt'];
      console.log('System Prompt:', systemPrompt);
      this.$copilotPane.systemMessage = {
        ...this.$copilotPane.systemMessage,
        [context.type]: systemPrompt
      }
      // Here you can save the system prompt to the service or state as needed
    }
  }
}
